import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpenCheck, ClipboardCheck, Target, Award } from 'lucide-react';
import { THEMES, MODULES, loadProgress, themeProgressPct, fmt, PASS } from '../data/learning';
import { moduleMinutes } from '../data/duration';
import { ThemeGlyph, Track, ComparePanel } from '../components/ui/learnbits';
import { ThemeIllustration } from '../components/ui/illustrations';
import { Reveal, CountUp } from '../components/ui/motion';
import { useAuth } from '../state/auth';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const store = loadProgress();

  const cont = THEMES.find((t) => { const p = themeProgressPct(t.id); return p > 0 && p < 100; }) || THEMES[0];
  const contIdx = Math.min(store[cont.id].doneModules, cont.mods - 1);
  const contMod = MODULES[cont.id][contIdx];
  const contPct = themeProgressPct(cont.id);
  const started = (Object.values(store) as { doneModules: number; sa: number | null; toets: number | null }[])
    .some((s) => s.doneModules > 0 || s.sa != null || s.toets != null);

  // Kerncijfers voor de stats-strip
  const totalMods = THEMES.reduce((s, t) => s + t.mods, 0);
  const doneMods = THEMES.reduce((s, t) => s + store[t.id].doneModules, 0);
  const passed = THEMES.filter((t) => store[t.id].toets != null && (store[t.id].toets as number) >= PASS).length;
  const scans = THEMES.filter((t) => store[t.id].sa != null).length;

  const firstName = user?.name?.split(/\s+/)[0];
  const statusOf = (pct: number) => (pct >= 100 ? { label: 'Afgerond', cls: 'chip-success' } : pct > 0 ? { label: 'Bezig', cls: 'chip-blue' } : { label: 'Nog te starten', cls: 'chip-muted' });

  const STATS = [
    { icon: BookOpenCheck, label: 'Modules afgerond', value: doneMods, suffix: ` / ${totalMods}`, color: '#2440CF', bg: '#EAEEFC' },
    { icon: Target, label: 'Zelfscans gedaan', value: scans, suffix: ' / 5', color: '#C29A33', bg: '#F6EFD9' },
    { icon: ClipboardCheck, label: 'Toetsen gehaald', value: passed, suffix: ' / 5', color: '#2B7A4B', bg: '#E3F0E8' },
    { icon: Award, label: 'Certificaten verdiend', value: passed, suffix: '', color: '#8A2C3D', bg: '#F6E7EA' },
  ];

  return (
    <div className="shell py-12 sm:py-16">
      {/* Begroeting */}
      <p className="label mb-3 anim-rise">{firstName ? 'Welkom terug' : 'Jouw leeromgeving'}</p>
      <h1 className="font-display font-bold text-ink mb-8 anim-rise" style={{ fontSize: 'clamp(32px,5.5vw,48px)', letterSpacing: '-0.02em', lineHeight: 1.05, animationDelay: '60ms' }}>
        {firstName ? `Fijn dat je er weer bent, ${firstName}.` : 'Word wegwijs in digitaal en AI.'}
      </h1>

      {/* Hero: ga verder waar je gebleven was */}
      <section className="rounded-xl p-7 sm:p-10 text-white mb-8 anim-rise" style={{ background: '#2440CF', position: 'relative', overflow: 'hidden', animationDelay: '120ms' }}>
        {/* Rustig zwevende accenten */}
        <span aria-hidden className="ill-float" style={{ position: 'absolute', right: -36, top: -36, width: 190, height: 190, borderRadius: '50%', border: '2px solid rgba(233,199,102,.35)' }} />
        <span aria-hidden className="ill-float-b" style={{ position: 'absolute', right: 96, bottom: -58, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,.06)' }} />
        <span aria-hidden style={{ position: 'absolute', right: 54, top: 42, width: 14, height: 14, borderRadius: '50%', background: '#E9C766' }} className="ill-float-b" />

        <div style={{ position: 'relative' }}>
          <p className="font-sans font-semibold uppercase mb-4" style={{ fontSize: 13, letterSpacing: '0.16em', color: '#E9C766' }}>
            {started ? 'Ga verder waar je gebleven was' : 'Begin hier'}
          </p>
          <h2 className="font-display font-bold mb-3 text-white" style={{ fontSize: 'clamp(26px,4vw,38px)', lineHeight: 1.1 }}>{contMod.title}</h2>
          <p className="mb-7" style={{ fontSize: 18, color: 'rgba(255,255,255,.82)' }}>{cont.name} · Module {contIdx + 1} van {cont.mods} · ongeveer {moduleMinutes(cont.id, contIdx)} min</p>
          <div className="flex items-center gap-5 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <span className="block w-full rounded-full overflow-hidden" style={{ height: 10, background: 'rgba(255,255,255,.22)' }}>
                <span className="block h-full rounded-full" style={{ width: `${contPct}%`, background: '#E9C766', transition: 'width 1s cubic-bezier(.2,.7,.2,1)' }} />
              </span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {store[cont.id].sa == null && (
                <button onClick={() => navigate(`/thema/${cont.id}/zelfscan`)} className="btn" style={{ fontSize: 15.5, background: 'rgba(255,255,255,.14)', color: '#fff', border: '1px solid rgba(255,255,255,.35)' }}>
                  Doe eerst de zelfscan (± 1 min)
                </button>
              )}
              <button onClick={() => navigate(`/thema/${cont.id}/les/${contIdx}`)} className="btn bg-white text-ink" style={{ fontSize: 16 }}>
                {started ? 'Verder leren' : 'Start de eerste module'} <ArrowRight className="w-5 h-5 cta-arrow" strokeWidth={1.9} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Kerncijfers */}
      <div className="grid gap-4 mb-12" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))' }}>
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 70}>
            <div className="card p-5 flex items-center gap-4">
              <span className="grid place-items-center rounded-tile flex-none" style={{ width: 46, height: 46, background: s.bg }}>
                <s.icon className="w-5 h-5" style={{ color: s.color }} strokeWidth={1.75} />
              </span>
              <div>
                <p className="font-display font-bold text-ink" style={{ fontSize: 26, lineHeight: 1 }}>
                  <CountUp to={s.value} /><span className="text-muted" style={{ fontSize: 15, fontWeight: 600 }}>{s.suffix}</span>
                </p>
                <p className="text-sm text-sub mt-1">{s.label}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Thema's */}
      <div className="flex items-end justify-between mb-6">
        <h2 className="font-display font-bold text-ink" style={{ fontSize: 'clamp(22px,3vw,28px)' }}>Vijf thema’s</h2>
        <Link to="/themas" className="text-sm font-medium text-royal hover:underline">Alle thema’s</Link>
      </div>
      <div className="grid gap-5 mb-12" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(270px,1fr))' }}>
        {THEMES.map((t, i) => {
          const pct = themeProgressPct(t.id);
          const st = statusOf(pct);
          const score = store[t.id].toets != null ? `Toets ${fmt(store[t.id].toets)}` : store[t.id].sa != null ? `Zelfscan ${fmt(store[t.id].sa)}` : 'Nog niet gestart';
          return (
            <Reveal key={t.id} delay={i * 70}>
              <Link to={`/thema/${t.id}`} className="card card-hover flex flex-col overflow-hidden h-full">
                <div style={{ background: '#FBFAF7', borderBottom: '1px solid #F0EBE3' }}>
                  <ThemeIllustration themeId={t.id} style={{ width: '100%', height: 128, display: 'block' }} />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <ThemeGlyph theme={t} size={40} />
                    <span className={st.cls}>{st.label}</span>
                  </div>
                  <h3 className="font-display font-semibold text-ink mb-2" style={{ fontSize: 20 }}>{t.name}</h3>
                  <p className="text-sub mb-5 flex-1">{t.desc}</p>
                  <Track pct={pct} color={t.accent} />
                  <div className="flex items-center justify-between mt-3 text-sm text-muted">
                    <span>{t.mods} modules</span>
                    <span style={{ color: t.textc }}>{score}</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold" style={{ color: t.accent }}>
                    {pct > 0 ? 'Verder met dit thema' : 'Ontdek dit thema'} <ArrowRight className="w-4 h-4 cta-arrow" strokeWidth={2} />
                  </span>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>

      <Reveal><ComparePanel /></Reveal>
    </div>
  );
};
